// Pieces shared by the Shared credits and Usage sheets: grouped lists, an
// initials avatar, the status tag, the progress bar and the share summary card.

import React from "react";
import { ChevronRight } from "lucide-react";
import { cn } from "../../lib/utils";

/** A titled block inside a sheet: small uppercase heading, bordered list. */
export const SheetGroup = ({ title, aside, children, className }) => (
  <section className="grid min-w-0 gap-2">
    {(title || aside) && (
      <div className="flex items-baseline justify-between gap-2 px-0.5">
        {title && (
          <h3 className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">{title}</h3>
        )}
        {aside && <span className="truncate text-xs text-muted-foreground">{aside}</span>}
      </div>
    )}
    <div className={cn("divide-y divide-border overflow-hidden rounded-xl border border-border bg-background", className)}>
      {children}
    </div>
  </section>
);

/** Label on the left, current value on the right, opens a choice. */
export const ValueRow = ({ label, value, onClick, valueClassName }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={!onClick}
    className="grid w-full grid-cols-[auto_minmax(0,1fr)_1rem] items-center gap-2.5 px-3 py-3 text-left transition-colors hover:bg-muted/30 disabled:cursor-default disabled:hover:bg-transparent"
  >
    <span className="whitespace-nowrap text-sm font-medium">{label}</span>
    <span className={cn("truncate text-right text-[13px]", valueClassName)}>{value}</span>
    {onClick ? <ChevronRight className="h-4 w-4 text-muted-foreground/70" /> : <span />}
  </button>
);

export const initials = (name) =>
  String(name || "?")
    .trim()
    .split(/\s+/)
    .map((part) => part[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

/** A person's initials in a circle: identity without a photo to load. */
export const Avatar = ({ name, size = 36, muted = false }) => (
  <span
    aria-hidden="true"
    className={cn(
      "flex shrink-0 items-center justify-center rounded-full font-semibold",
      muted ? "bg-border text-muted-foreground" : "bg-muted text-foreground",
    )}
    style={{ width: size, height: size, fontSize: Math.round(size * 0.36) }}
  >
    {initials(name)}
  </span>
);

const TAG_TONES = {
  warning: "bg-yellow-500/15 text-yellow-500",
  danger: "bg-destructive/15 text-red-400",
  primary: "bg-primary/10 text-primary",
};

/** A short status word. The colour backs up the word, never replaces it. */
export const StatusTag = ({ tag }) =>
  tag ? (
    <span className={cn("whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-semibold", TAG_TONES[tag.tone])}>
      {tag.label}
    </span>
  ) : null;

// Blue by default (the "your key" series colour in Usage); amber from 80%,
// red at the cap, each with a tag saying so.
const METER_TONES = { normal: "bg-[#3987e5]", warning: "bg-yellow-500", danger: "bg-destructive" };

export const Meter = ({ meter, thick = false, label }) =>
  meter ? (
    <span
      role="meter"
      aria-label={label}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(meter.pct)}
      className={cn("block w-full overflow-hidden rounded-full bg-border", thick ? "h-1.5" : "h-1")}
    >
      <span className={cn("block h-full rounded-full", METER_TONES[meter.tone])} style={{ width: `${meter.pct}%` }} />
    </span>
  ) : null;

/**
 * One share in a list: avatar, who and when it ends, what's left, a thin bar.
 * `figure` is the right-hand amount and `figureLabel` the word under it.
 */
export const ShareRow = ({ name, detail, tag, figure, figureLabel, meter, onClick, avatarName }) => (
  <button
    type="button"
    onClick={onClick}
    className="flex w-full flex-col gap-2.5 px-3 py-3 text-left transition-colors hover:bg-muted/30"
  >
    <span className="grid w-full grid-cols-[2.25rem_minmax(0,1fr)_auto_1rem] items-center gap-2.5">
      <Avatar name={avatarName || name} />
      <span className="min-w-0">
        <span className="block truncate text-sm font-medium">{name}</span>
        {/* The tag leads the second line so a long name keeps its width. */}
        <span className="flex min-w-0 items-center gap-1.5">
          <StatusTag tag={tag} />
          <span className="truncate text-xs text-muted-foreground">{detail}</span>
        </span>
      </span>
      <span className="text-right">
        <span className="block text-sm font-semibold tabular-nums">{figure}</span>
        <span className="block text-[11px] text-muted-foreground">{figureLabel}</span>
      </span>
      <ChevronRight className="h-4 w-4 text-muted-foreground/70" />
    </span>
    <Meter meter={meter} label={`${name}: ${figure} ${figureLabel}`} />
  </button>
);

/** The summary card a share opens with: who, one big figure, the bar, three facts. */
export const ShareCard = ({ name, avatarName, detail, tag, hero, heroLabel, meter, stats }) => (
  <div className="grid min-w-0 grid-cols-[minmax(0,1fr)] gap-3.5 rounded-2xl border border-border bg-background p-4">
    <div className="flex min-w-0 items-center gap-3">
      <Avatar name={avatarName || name} size={44} />
      <span className="min-w-0 flex-1">
        <span className="block truncate text-base font-semibold">{name}</span>
        {/* As in ShareRow: the tag leads the second line, never squeezes the name. */}
        <span className="flex min-w-0 items-center gap-1.5">
          <StatusTag tag={tag} />
          <span className="truncate text-xs text-muted-foreground">{detail}</span>
        </span>
      </span>
    </div>
    <p className="m-0">
      <span className="text-[40px] font-semibold leading-none tracking-tight">{hero}</span>
      <span className="text-sm text-muted-foreground"> {heroLabel}</span>
    </p>
    <Meter meter={meter} thick label={`${hero} ${heroLabel}`} />
    <div className="grid grid-cols-[repeat(3,minmax(0,1fr))] gap-2">
      {stats.map((stat) => (
        <div key={stat.label} className="rounded-[10px] bg-card px-2.5 py-2">
          <div className="text-[11px] text-muted-foreground">{stat.label}</div>
          <div className="truncate text-sm font-semibold tabular-nums">{stat.value}</div>
        </div>
      ))}
    </div>
  </div>
);

/** A day heading inside a grouped list. */
export const DayHeading = ({ children }) => (
  <div className="bg-card px-3 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
    {children}
  </div>
);

/** A line in an activity list: what, a detail line, and an amount on the right. */
export const ActivityRow = ({ title, detail, amount, note, icon: Icon, badge }) => (
  <div
    className={cn(
      "grid items-center gap-2.5 px-3 py-2.5",
      Icon ? "grid-cols-[1.875rem_minmax(0,1fr)_auto]" : "grid-cols-[minmax(0,1fr)_auto]",
    )}
  >
    {Icon && (
      <span className="flex h-[30px] w-[30px] items-center justify-center rounded-lg bg-card">
        <Icon className="h-4 w-4 text-muted-foreground" />
      </span>
    )}
    <span className="min-w-0">
      <span className="flex min-w-0 items-center gap-1.5">
        <span className="truncate text-sm">{title}</span>
        {badge}
      </span>
      {detail && <span className="block truncate text-xs text-muted-foreground">{detail}</span>}
    </span>
    <span className="text-right font-mono text-xs tabular-nums">
      <span className="block">{amount}</span>
      {note && <span className="block text-[11px] text-muted-foreground">{note}</span>}
    </span>
  </div>
);
